import { removeUserDevice } from '../clinicPortalServices/loginService';
import { goPatientLogin } from '../patientPortalServices/loginService';
import { isUserLoggedIn, getPatientAuthToken, getUserAuthToken } from './util';
import axios from 'axios';
import { serviceConstants } from './constants';

export const handleError = (error) => {
    if (error.status === 401) {
        if (isUserLoggedIn()) {
            if (error.message === 'Token has wrong Device ID') {
                window.localStorage.clear();
                window.location.href = '/';
            } else {
                removeUserDevice();
                window.location.href = '/';
            }
        } else if (getPatientAuthToken()) {
            goPatientLogin();
        }
        // Not logged in (e.g. mid-login): a stray 401 must NOT clear
        // storage or redirect — that would abort the in-progress login.
    }
};

// A known, intermittent connection-race in the backend occasionally returns 401
// "Token has wrong Device ID" for a perfectly valid, logged-in session - bursts of
// parallel requests right after login are the usual trigger. A single immediate
// retry overwhelmingly succeeds, so that case gets one retry before we treat it as
// a real logout instead of dropping the user's session over a transient blip.
const isDeviceIdRaceError = (error) =>
    error.response && error.response.status === 401 && error.response.data?.message === 'Token has wrong Device ID';

const handleRequestError = (error, method, url) => {
    console.error(`API Error on ${method} ${url}:`, error);
    if (error.response) {
        // For migration tool, return error data instead of redirecting
        if (url.includes('/migrationTool/')) {
            return {
                success: false,
                message:
                    error.response.data?.message ||
                    error.response.data?.error ||
                    error.message ||
                    'An unknown error occurred',
                error: error.response.data
            };
        }
        handleError(error.response);
    } else if (error.request) {
        // Request was made but no response
        console.error('No response received:', error.request);
        return {
            success: false,
            message: 'No response from server. Please check your connection.',
            error: error
        };
    } else {
        // Something else happened
        console.error('Request setup error:', error.message);
        return {
            success: false,
            message: error.message || 'Failed to make request',
            error: error
        };
    }
};

export const apiInterfaceForJson = (url, method, data, signal) => {
    let token = getUserAuthToken();
    if (!token) {
        token = getPatientAuthToken();
    }
    const requestConfig = {
        url: serviceConstants.API_HOST_NAME + url,
        method: method,
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        data: data,
        signal: signal
    };
    return axios
        .request(requestConfig)
        .then((response) => response.data)
        .catch((error) => {
            if (isDeviceIdRaceError(error)) {
                return axios
                    .request(requestConfig)
                    .then((response) => response.data)
                    .catch((retryError) => handleRequestError(retryError, method, url));
            }
            return handleRequestError(error, method, url);
        });
};

export const apiInterfaceForFormData = (url, method, formData) => {
    let token = getUserAuthToken();
    if (!token) {
        token = getPatientAuthToken();
    }
    const requestConfig = {
        url: serviceConstants.API_HOST_NAME + url,
        method: method,
        headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: 'Bearer ' + token
        },
        data: formData
    };
    return axios
        .request(requestConfig)
        .then((response) => response.data)
        .catch((error) => {
            if (isDeviceIdRaceError(error)) {
                return axios
                    .request(requestConfig)
                    .then((response) => response.data)
                    .catch((retryError) => handleRequestError(retryError, method, url));
            }
            return handleRequestError(error, method, url);
        });
};
